import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Autocomplete, TextField } from '@mui/material';
import { debounce } from 'lodash';
import { inputsStyle } from './styles';
import { InputMinLoader } from './inputMiniLoader';
import { FooterPaper } from './autocompleteSelectInput';
import { ErrMessage } from '../messages';
import { Images, useGlobalStyles } from 'utils';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Official descriptions for codes already chosen (a payer service's chips): one `GET /icd10?codes=`
 * for the codes not yet known; `remember(option)` adds a code just picked without a request.
 */
export const useIcd10Descriptions = (codes = []) => {
    const [descriptions, setDescriptions] = useState({});
    const missing = codes.filter((code) => code && !(code in descriptions));

    useEffect(() => {
        if (!missing.length) return;
        let cancelled = false;
        axios
            .get('/icd10', { auth: true, params: { codes: missing.join(',') } })
            .then((response) => {
                if (cancelled) return;
                const found = Object.fromEntries((response.data || []).map((row) => [row.code, row.description]));
                // Unknown codes map to '' so they are not requested again
                setDescriptions((prev) => ({ ...prev, ...Object.fromEntries(missing.map((code) => [code, found[code] || ''])) }));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [missing.join(',')]);

    const remember = (option) => option?.code && setDescriptions((prev) => ({ ...prev, [option.code]: option.description || '' }));

    return { descriptions, remember };
};

/**
 * ICD-10-CM code picker over the official code set (`GET /icd10?search=`), the same list the api
 * validates against. No free-text entry in the field itself; a page may pass `onAddCustom` (a dropdown
 * footer, "Add Diagnosis Code") and list what it adds in `customOptions`.
 *
 * Same MUI Autocomplete + input styles as the other shared selectors (see
 * `selectAutocompletePagination.js`). Like that component it calls the api itself: it is a shared
 * input, not a page, and the search is stateless.
 *
 * @param value         the picked code (`'F84.0'`) or empty
 * @param onSelect      `(option | null) => void`, option = `{ code, description }`
 * @param exclude       codes already chosen (hidden from the results, e.g. a service's chips)
 * @param clearOnSelect pick-and-add mode: the field empties after every pick
 * @param onAddCustom   `(typed) => void` — shows the "Add Diagnosis Code" footer
 * @param customOptions `[{ code, description }]` added from that footer, listed above the search results
 */
export const Icd10CodeSelect = ({
    label,
    placeholder = 'Search by code or description',
    value,
    onSelect,
    exclude = [],
    clearOnSelect,
    disabled,
    error,
    noError,
    onAddCustom,
    customOptions = [],
}) => {
    const classes = inputsStyle();
    const globalStyles = useGlobalStyles();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const selected = value ? options.find((option) => option.code === value) || { code: value, description: '' } : null;

    const search = useMemo(
        () =>
            debounce(async (term) => {
                try {
                    const response = await axios.get('/icd10', { auth: true, params: { search: term.trim() } });
                    setOptions(response.data || []);
                } catch (e) {
                    setOptions([]);
                } finally {
                    setLoading(false);
                }
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => search.cancel(), [search]);

    const handleInput = (event, term, reason) => {
        setInputValue(term);
        if (reason === 'input' && term.trim()) {
            setLoading(true);
            search(term);
        } else if (!term) {
            search.cancel();
            setOptions([]);
            setLoading(false);
        }
    };

    // Added codes that fit what is typed (all of them while the field is empty), then the search results
    const term = inputValue.trim().toLowerCase();
    const matchingCustom = customOptions.filter(
        (option) =>
            !term ||
            option.code.toLowerCase().replace('.', '').startsWith(term.replace('.', '')) ||
            option.description.toLowerCase().includes(term)
    );
    const listed = [...matchingCustom, ...options.filter((option) => !matchingCustom.some((custom) => custom.code === option.code))];

    const handleAddCustom = () => {
        setOpen(false);
        onAddCustom(inputValue.trim());
    };

    const handleChange = (event, option) => {
        onSelect(option || null);
        if (clearOnSelect) {
            setInputValue('');
            setOptions([]);
        }
    };

    return (
        <div>
            <Autocomplete
                style={{ width: '100%' }}
                className={
                    disabled
                        ? classes.inputTextFieldDisabled
                        : error
                        ? globalStyles.inputTextFieldError
                        : value
                        ? globalStyles.inputTextFieldBlue
                        : classes.inputTextField
                }
                options={listed.filter((option) => !exclude.includes(option.code))}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                slots={onAddCustom ? { paper: FooterPaper } : undefined}
                slotProps={
                    onAddCustom
                        ? {
                              paper: {
                                  footer: (
                                      <button type="button" className="icd10-add-custom" onClick={handleAddCustom}>
                                          <img src={Images.addBluePlus} alt="" />
                                          Add Diagnosis Code
                                      </button>
                                  ),
                              },
                          }
                        : undefined
                }
                value={clearOnSelect ? null : selected}
                inputValue={clearOnSelect ? inputValue : undefined}
                onInputChange={handleInput}
                onChange={handleChange}
                getOptionLabel={(option) => (option?.code ? `${option.code} ${option.description}`.trim() : '')}
                isOptionEqualToValue={(option, current) => option?.code === current?.code}
                filterOptions={(x) => x}
                loading={loading}
                disabled={disabled}
                noOptionsText={inputValue.trim() ? 'Not an ICD-10-CM code' : 'Type a code or a description'}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                        <li key={key} {...rest}>
                            <span className="icd10-option-code">{option.code}</span>
                            <span className="icd10-option-description">{option.description}</span>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        variant="outlined"
                        slotProps={{
                            input: { ...params.InputProps, endAdornment: loading ? <InputMinLoader /> : params.InputProps.endAdornment },
                        }}
                    />
                )}
            />
            {!noError && <ErrMessage text={error || ''} style={{ position: 'relative' }} />}
        </div>
    );
};
